import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { MultiSelectCombobox } from '@/components/custom/multi-select-combobox'
import { useUsersSearch } from '@/hooks/use-reference-data'
import { Typography } from '@/components/ui/typography'

export interface BoardMemberData {
  id: string
  name: string
}

interface AddBoardMemberModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBoardMemberIds: string[]
  onSave: (userIds: string[], members: BoardMemberData[]) => Promise<void>
  canEdit: boolean
  isEditing?: boolean
}

export function AddBoardMemberModal({
  open,
  onOpenChange,
  currentBoardMemberIds,
  onSave,
  canEdit,
  isEditing = false,
}: AddBoardMemberModalProps) {
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([])

  // Reset selection when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedUserIds(currentBoardMemberIds)
    }
  }, [open, currentBoardMemberIds])

  const { data: usersData, isLoading: usersLoading } = useUsersSearch({
    search: '',
    enabled: open,
  })

  const usersAsOptions = React.useMemo(() => {
    return (
      usersData?.data.map((user) => ({
        value: user.id,
        label: user.name,
      })) ?? []
    )
  }, [usersData])

  // Check if selection has changed from original
  const hasChanges = React.useMemo(() => {
    if (selectedUserIds.length !== currentBoardMemberIds.length) return true
    const sortedSelected = [...selectedUserIds].sort()
    const sortedCurrent = [...currentBoardMemberIds].sort()
    return !sortedSelected.every((id, idx) => id === sortedCurrent[idx])
  }, [selectedUserIds, currentBoardMemberIds])

  const handleSave = () => {
    if (!canEdit || !hasChanges) return

    const selectedMembers = selectedUserIds
      .map((id) => {
        const user = usersData?.data.find((u) => u.id === id)
        return user ? { id: user.id, name: user.name } : null
      })
      .filter((m): m is BoardMemberData => m !== null)

    // Close modal immediately for optimistic UI
    onOpenChange(false)
    // Fire and forget - errors handled via toast in parent
    void onSave(selectedUserIds, selectedMembers)
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-100 px-4 py-5">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Update Board Members' : 'Add Board Members'}
          </DialogTitle>
          <DialogDescriptionHidden>
            Select team members to add as board members for this company
          </DialogDescriptionHidden>
        </DialogHeader>

        <MultiSelectCombobox
          options={usersAsOptions}
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          placeholder="Search to find or add a team member"
          disabled={!canEdit}
          isLoading={usersLoading}
        />

        {!canEdit ? (
          <Typography variant="body-sm" className="text-destructive">
            You do not have permission to update board members for this company.
          </Typography>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline-muted"
            size="sm"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canEdit || !hasChanges}
          >
            {isEditing ? 'Update Board Members' : 'Add Board Members'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
